import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  MenuItem,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import OverlineLabel from 'components/common/OverlineLabel';
import CampaignSchedulePicker from 'components/controls/CampaignSchedulePicker';
import { bool, func, object } from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  useListInitiativesQuery,
  useCreateInitiativeMutation,
} from 'store/app/entities/InitiativesSlice';
import { I18n } from 'utils/i18n';

const POPOVER_WIDTH = 320;
const POPOVER_HEIGHT = 366;

const popoverPaperSx = theme => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  padding: 0,
  width: POPOVER_WIDTH,
  height: POPOVER_HEIGHT,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[8],
  borderRadius: '12px',
  flex: 'none',
  zIndex: 1,
});

const searchFieldSx = theme => ({
  flex: 1,
  minWidth: 0,
  '& .MuiInputBase-root': {
    fontSize: 15,
    paddingRight: 0,
  },
  '& .MuiInputBase-input': {
    padding: theme.spacing(0.75, 0),
    '&::placeholder': { opacity: 0.8 },
  },
  '& .MuiOutlinedInput-notchedOutline': { display: 'none' },
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent',
    '&:hover .MuiOutlinedInput-notchedOutline': { display: 'none' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { display: 'none' },
  },
});

const listItemSx = theme => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 2),
});

const listItemDotBaseSx = {
  flexShrink: 0,
  width: 8,
  height: 8,
  borderRadius: '50%',
};

const getInitiativeDotSx = (option, selected, theme) => {
  if (option?.status === 'inactive') {
    return {
      ...listItemDotBaseSx,
      backgroundColor: 'transparent',
      border: `2px solid ${theme.palette.action.disabled}`,
      boxSizing: 'border-box',
    };
  }
  const active =
    !(
      option?.status === 'draft' ||
      option?.status === 'finished' ||
      option?.status === 'inactive'
    ) &&
    (() => {
      const now = new Date();
      const start = option?.startDate || option?.start_date;
      const end = option?.endDate || option?.end_date;
      if (start && new Date(start) > now) return false;
      if (end && new Date(end) < now) return false;
      return true;
    })();
  if (active) {
    return {
      ...listItemDotBaseSx,
      backgroundColor: theme.palette.success.main,
      boxShadow: selected
        ? `0 0 0 2px ${theme.palette.success.main}40, 0 0 8px ${theme.palette.success.main}`
        : 'none',
    };
  }
  return {
    ...listItemDotBaseSx,
    backgroundColor: theme.palette.action.disabled,
    boxShadow: 'none',
  };
};

const CREATE_NEW_INITIATIVE_VALUE = '__create_new_initiative__';
const EMPTY_ARRAY = [];

const InitiativeTagSelector = ({ value, onChange, disabled = false, recentInitiatives = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const open = Boolean(anchorEl);
  const { data: options = EMPTY_ARRAY, isFetching: isSearching } = useListInitiativesQuery(
    { search: searchQuery },
    {
      skip: !open,
      refetchOnMountOrArgChange: false,
    },
  );
  const [createInitiative, { isLoading: isCreatingInitiative }] = useCreateInitiativeMutation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newInitiativeName, setNewInitiativeName] = useState('');
  const [newInitiativeSchedule, setNewInitiativeSchedule] = useState({
    startPreset: 'today',
    startDate: null,
    endPreset: 'none',
    endDate: null,
  });
  const [newInitiativeObjective, setNewInitiativeObjective] = useState('read');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const searchInputRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [open]);

  const handleOpen = useCallback(() => {
    setAnchorEl(triggerRef.current);
    setSearchQuery('');
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearchQuery('');
  }, []);

  const handleClear = useCallback(() => {
    onChange(null);
    handleClose();
  }, [onChange, handleClose]);

  const openCreateDialog = useCallback(() => {
    setDialogOpen(true);
    setNewInitiativeName(searchQuery?.trim() || '');
  }, [searchQuery]);

  const closeCreateDialog = useCallback(() => {
    setDialogOpen(false);
    setAdvancedOpen(false);
  }, []);

  const handleCreateInitiative = useCallback(async () => {
    if (!newInitiativeName.trim()) return;
    try {
      const createdInitiative = await createInitiative({
        name: newInitiativeName.trim(),
        startDate: newInitiativeSchedule.startDate,
        endDate: newInitiativeSchedule.endDate,
        objective: newInitiativeObjective,
      }).unwrap();
      onChange(createdInitiative);
      closeCreateDialog();
      handleClose();
    } catch {
      // createInitiative errors are surfaced by RTK Query; dialog stays open
    }
  }, [
    newInitiativeName,
    newInitiativeSchedule,
    newInitiativeObjective,
    createInitiative,
    onChange,
    closeCreateDialog,
    handleClose,
  ]);

  const handleSelectOption = useCallback(
    option => {
      if (option?.id === CREATE_NEW_INITIATIVE_VALUE) {
        handleClose();
        openCreateDialog();
        return;
      }
      onChange(option || null);
      handleClose();
    },
    [onChange, handleClose, openCreateDialog],
  );

  const showRecent = recentInitiatives.length > 0 && !searchQuery.trim();
  const createNewOption = {
    id: CREATE_NEW_INITIATIVE_VALUE,
    name: I18n.t('Initiative.Create new'),
  };

  const isInitiativeFinished = option => {
    const now = new Date();
    if (option?.status === 'finished') return true;
    const end = option?.endDate || option?.end_date;
    return !!(end && new Date(end) < now);
  };

  const getInitiativeStatusLabel = option => {
    const now = new Date();
    if (option?.status === 'finished') return 'Initiative.Finished';
    if (option?.status === 'draft' || option?.status === 'not_started') return 'Initiative.Draft';
    const start = option?.startDate || option?.start_date;
    if (start && new Date(start) > now) return 'Initiative.Not started';
    if (option?.status === 'inactive') return null;
    const end = option?.endDate || option?.end_date;
    if (end && new Date(end) < now) return 'Initiative.Finished';
    return null;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
      {value ? (
        <ButtonBase
          ref={triggerRef}
          onClick={handleOpen}
          disabled={disabled}
          aria-label={value.name}
          focusRipple
          sx={theme => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            padding: theme.spacing(0.25, 0.5, 0.25, 1),
            minHeight: 32,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            backgroundColor: theme.palette.background.paper,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          })}
        >
          <LocalOfferOutlinedIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontSize: 13,
              fontWeight: 500,
              maxWidth: 160,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value.name}
          </Typography>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              handleClear();
            }}
            aria-label={I18n.t('Initiative.Clear')}
            sx={theme => ({
              padding: theme.spacing(0.25),
              marginRight: -theme.spacing(0.25),
              marginLeft: theme.spacing(0.25),
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.action.hover,
              },
            })}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </ButtonBase>
      ) : (
        <IconButton
          ref={triggerRef}
          size="small"
          onClick={handleOpen}
          disabled={disabled}
          aria-label={I18n.t('Initiative.Create new')}
          sx={{ color: 'text.secondary' }}
        >
          <LocalOfferOutlinedIcon fontSize="small" />
        </IconButton>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: { sx: popoverPaperSx },
        }}
        disableRestoreFocus
      >
        <Box
          sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              handleClose();
              e.preventDefault();
            }
          }}
        >
          <Box
            sx={theme => ({
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(1.5),
              padding: theme.spacing(1, 2),
              borderBottom: `1px solid ${theme.palette.divider}`,
            })}
          >
            <Box
              component="span"
              aria-hidden
              sx={{
                flexShrink: 0,
                width: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& .MuiSvgIcon-root': { fontSize: 20 },
              }}
            >
              <SearchIcon fontSize="small" color="action" />
            </Box>
            <TextField
              sx={searchFieldSx}
              size="small"
              placeholder={I18n.t('Initiative.Search placeholder')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              inputRef={searchInputRef}
              variant="outlined"
              hiddenLabel
              fullWidth
              slotProps={{
                input: {
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setSearchQuery('')}
                        size="small"
                        aria-label={I18n.t('Initiative.Clear search')}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          </Box>
          <List sx={{ flex: 1, overflow: 'auto' }} disablePadding>
            {isSearching && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {!isSearching && showRecent && (
              <Box sx={theme => ({ padding: theme.spacing(0.5, 0) })}>
                <Typography
                  component="div"
                  sx={theme => ({
                    padding: theme.spacing(0.5, 2),
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: theme.palette.text.secondary,
                  })}
                >
                  {I18n.t('Initiative.Recent')}
                </Typography>
                {recentInitiatives.map(option => {
                  const selected = value?.id === option.id;
                  const statusLabelKey = getInitiativeStatusLabel(option);
                  return (
                    <ListItemButton
                      key={option.id}
                      sx={listItemSx}
                      selected={selected}
                      onClick={() => handleSelectOption(option)}
                      dense
                    >
                      <Box
                        component="span"
                        sx={{
                          flexShrink: 0,
                          width: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Box
                          component="span"
                          aria-hidden
                          sx={theme => getInitiativeDotSx(option, selected, theme)}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={theme => ({
                          flex: 1,
                          minWidth: 0,
                          ...(isInitiativeFinished(option) && {
                            textDecoration: 'line-through',
                            color: theme.palette.text.secondary,
                          }),
                        })}
                      >
                        {option.name}
                      </Typography>
                      {selected ? (
                        <CheckIcon color="primary" fontSize="small" />
                      ) : statusLabelKey ? (
                        <Box
                          component="span"
                          sx={theme => ({
                            flexShrink: 0,
                            padding: theme.spacing(0.25, 0.75),
                            borderRadius: theme.shape.borderRadius,
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.text.secondary,
                            fontSize: 12,
                            fontWeight: 500,
                          })}
                        >
                          {I18n.t(statusLabelKey)}
                        </Box>
                      ) : null}
                    </ListItemButton>
                  );
                })}
              </Box>
            )}

            {!isSearching && (
              <>
                {showRecent && recentInitiatives.length > 0 && (
                  <Typography
                    component="div"
                    sx={theme => ({
                      padding: theme.spacing(0.5, 2),
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: theme.palette.text.secondary,
                    })}
                  >
                    {I18n.t('Initiative.All')}
                  </Typography>
                )}
                {options.map(option => {
                  const selected = value?.id === option.id;
                  const statusLabelKey = getInitiativeStatusLabel(option);
                  return (
                    <ListItemButton
                      key={option.id}
                      sx={listItemSx}
                      selected={selected}
                      onClick={() => handleSelectOption(option)}
                      dense
                    >
                      <Box
                        component="span"
                        sx={{
                          flexShrink: 0,
                          width: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Box
                          component="span"
                          aria-hidden
                          sx={theme => getInitiativeDotSx(option, selected, theme)}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={theme => ({
                          flex: 1,
                          minWidth: 0,
                          ...(isInitiativeFinished(option) && {
                            textDecoration: 'line-through',
                            color: theme.palette.text.secondary,
                          }),
                        })}
                      >
                        {option.name}
                      </Typography>
                      {selected ? (
                        <CheckIcon color="primary" fontSize="small" />
                      ) : statusLabelKey ? (
                        <Box
                          component="span"
                          sx={theme => ({
                            flexShrink: 0,
                            padding: theme.spacing(0.25, 0.75),
                            borderRadius: theme.shape.borderRadius,
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.text.secondary,
                            fontSize: 12,
                            fontWeight: 500,
                          })}
                        >
                          {I18n.t(statusLabelKey)}
                        </Box>
                      ) : null}
                    </ListItemButton>
                  );
                })}
              </>
            )}
          </List>
          <ButtonBase
            onClick={() => handleSelectOption(createNewOption)}
            component="div"
            role="button"
            aria-label={I18n.t('Initiative.Create new')}
            sx={theme => ({
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: theme.spacing(1.5),
              borderTop: `1px solid ${theme.palette.divider}`,
              padding: theme.spacing(1.5, 2),
              ...(theme.palette.mode === 'dark'
                ? { backgroundColor: theme.palette.background.paper }
                : { backgroundColor: theme.palette.secondary.ultraUltraLight }),
            })}
          >
            <Box
              component="span"
              sx={theme => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                borderRadius: theme.shape.borderRadius,
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.text.secondary,
                '& .MuiSvgIcon-root': { fontSize: 18 },
              })}
            >
              <AddIcon />
            </Box>
            <Typography
              component="span"
              sx={theme => ({
                display: 'flex',
                alignItems: 'center',
                padding: theme.spacing(0.5, 0),
                fontSize: 14,
              })}
            >
              {I18n.t('Initiative.Create new')}
            </Typography>
          </ButtonBase>
        </Box>
      </Popover>
      <Dialog
        open={dialogOpen}
        onClose={closeCreateDialog}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: theme => ({
              borderRadius: '12px',
              border: `1px solid ${theme.palette.divider}`,
              maxWidth: 440,
            }),
          },
        }}
      >
        <DialogTitle
          sx={theme => ({
            borderBottom: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(1.5, 3),
            display: 'flex',
            alignItems: 'center',
          })}
        >
          {I18n.t('Initiative.New Initiative')}
          <IconButton
            aria-label="close"
            onClick={closeCreateDialog}
            size="small"
            sx={theme => ({
              marginLeft: 'auto',
              marginRight: -theme.spacing(0.5),
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={theme => ({
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1.5),
            padding: theme.spacing(3),
            marginTop: theme.spacing(2),
          })}
        >
          <Box
            sx={theme => ({ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) })}
          >
            <OverlineLabel>{I18n.t('Initiative.Name')}</OverlineLabel>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={I18n.t('Initiative.Name placeholder')}
              value={newInitiativeName}
              onChange={event => setNewInitiativeName(event.target.value)}
            />
          </Box>
          <ButtonBase
            onClick={() => setAdvancedOpen(prev => !prev)}
            disableRipple
            sx={theme => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(0.5),
              color: theme.palette.primary.main,
              fontSize: 14,
              fontWeight: 500,
              padding: theme.spacing(0.25, 0),
              textAlign: 'left',
              alignSelf: 'flex-start',
              '&:hover': {
                backgroundColor: 'transparent',
                color: theme.palette.primary.dark,
              },
            })}
          >
            {advancedOpen ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <KeyboardArrowRightIcon fontSize="small" />
            )}
            <Typography component="span" variant="body2">
              {I18n.t('Initiative.Advanced configuration')}
            </Typography>
          </ButtonBase>
          {advancedOpen && (
            <Box
              sx={theme => ({
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(1.5),
                paddingTop: theme.spacing(0.5),
              })}
            >
              <CampaignSchedulePicker
                value={newInitiativeSchedule}
                onChange={setNewInitiativeSchedule}
              />
              <div>
                <OverlineLabel>{I18n.t('Initiative.Goal')}</OverlineLabel>
                <TextField
                  fullWidth
                  size="small"
                  select
                  value={newInitiativeObjective}
                  onChange={event => setNewInitiativeObjective(event.target.value)}
                >
                  <MenuItem value="read">{I18n.t('Initiative.Goal read')}</MenuItem>
                  <MenuItem value="link_click">{I18n.t('Initiative.Goal click')}</MenuItem>
                  <MenuItem value="answer">{I18n.t('Initiative.Goal reply')}</MenuItem>
                </TextField>
              </div>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={theme => ({
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : theme.palette.secondary.ultraUltraLight,
            padding: theme.spacing(1.5, 3),
            gap: theme.spacing(1.5),
          })}
        >
          <Button onClick={closeCreateDialog} variant="outlined" color="secondary">
            {I18n.t('Initiative.Cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateInitiative}
            disabled={isCreatingInitiative || !newInitiativeName.trim()}
          >
            {I18n.t('Initiative.Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

InitiativeTagSelector.propTypes = {
  value: object,
  onChange: func.isRequired,
  disabled: bool,
};

export default InitiativeTagSelector;
